import React, { useState } from 'react';
import { Mail, Instagram, Linkedin, MapPin, Send, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Button from '../components/ui/Button';
import { motion } from 'framer-motion';

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: 'Sponsorluk',
        message: ''
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        const mailtoLink = `mailto:mskuturistrehberligihesabi@gmail.com?subject=${encodeURIComponent(formData.subject)} - ${encodeURIComponent(formData.name)}&body=${encodeURIComponent(formData.message)}%0D%0A%0D%0Aİletişim: ${encodeURIComponent(formData.email)}`;
        window.location.href = mailtoLink;
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Stagger animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1
        }
    };

    return (
        <div className="bg-[#172554] min-h-screen font-sans text-gray-200 relative overflow-hidden selection:bg-cyan-500/30 selection:text-cyan-200">
            <Navbar />

            {/* Dynamic Background Effects */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px] opacity-50 animate-pulse" />
                <div className="absolute bottom-0 left-0 -mb-40 -ml-20 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[100px] opacity-40" />
            </div>

            <main className="pt-32 pb-20 px-6 relative z-10">
                {/* Header Section */}
                <section className="container mx-auto max-w-4xl text-center mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        <span className="inline-block py-1 px-3 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-semibold mb-6 tracking-wide">
                            BİZE ULAŞIN
                        </span>
                        <h1 className="text-5xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white via-cyan-100 to-gray-400 mb-8 font-heading tracking-tight leading-tight">
                            Birlikte <span className="text-cyan-400 italic">Harika İşler</span><br />Başarabiliriz
                        </h1>
                        <p className="text-lg md:text-xl text-gray-400 leading-relaxed max-w-2xl mx-auto px-4 font-light">
                            Topluluğumuzun vizyonuna katkı sağlamak, sponsor olmak veya sadece merhaba demek için buradayız.
                        </p>
                    </motion.div>
                </section>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="container mx-auto max-w-6xl"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-24">
                        {/* Contact Cards */}
                        {[
                            {
                                icon: Mail,
                                title: "E-posta",
                                value: "mskuturistrehberligihesabi@gmail.com",
                                link: "mailto:mskuturistrehberligihesabi@gmail.com",
                                color: "cyan",
                                delay: 0
                            },
                            {
                                icon: Instagram,
                                title: "Instagram",
                                value: "@mskuturet",
                                link: "https://www.instagram.com/mskuturet?igsh=MTN5aG9taXRzYjEwZA==",
                                color: "pink",
                                delay: 1
                            },
                            {
                                icon: Linkedin,
                                title: "LinkedIn",
                                value: "MSKÜ TURET",
                                link: "https://www.linkedin.com/in/msk%C3%BC-turist-rehberli%C4%9Fi-toplulu%C4%9Fu-809140357/?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app",
                                color: "blue",
                                delay: 2
                            }
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                whileHover={{ y: -10 }}
                                className="group relative bg-white/5 backdrop-blur-md p-8 rounded-3xl border border-white/10 overflow-hidden text-center transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/10 hover:border-white/20"
                            >
                                <div className={`absolute inset-0 bg-gradient-to-br from-${item.color}-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                                <div className="relative z-10">
                                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:scale-110 group-hover:bg-white/10 transition-all duration-300">
                                        <item.icon className={`w-8 h-8 text-${item.color === 'pink' ? 'rose' : (item.color === 'emerald' ? 'emerald' : 'cyan')}-400 transition-colors`} />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                                    <a href={item.link} className="text-gray-400 hover:text-white transition-colors text-sm break-words bloc font-medium">
                                        {item.value}
                                    </a>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Enhanced Contact Form */}
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-24 items-start">
                        {/* Form Sidebar / Info */}
                        <div className="lg:col-span-2 space-y-8">
                            <motion.div variants={itemVariants} className="bg-gradient-to-br from-cyan-950/50 to-slate-900/50 p-8 rounded-3xl border border-white/10 backdrop-blur-md">
                                <h3 className="text-2xl font-bold text-white mb-4">Sıkça Sorulan Sorular</h3>
                                <div className="space-y-6">
                                    <div>
                                        <h4 className="text-cyan-400 font-semibold mb-2 text-sm">SPONSORLUK SÜRECİ NASIL?</h4>
                                        <p className="text-gray-400 text-sm leading-relaxed">
                                            Etkinliklerimize sponsor olmak için formu doldurmanız yeterli. Yönetim ekibimiz 24 saat içinde dönüş yapacaktır.
                                        </p>
                                    </div>

                                </div>
                            </motion.div>
                        </div>

                        {/* Main Form */}
                        <motion.div variants={itemVariants} className="lg:col-span-3">
                            <div className="bg-white/[0.03] backdrop-blur-xl rounded-[2rem] p-8 md:p-12 border border-white/10 shadow-2xl relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-emerald-500" />

                                <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
                                    Mesaj Gönder
                                    <span className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />
                                </h2>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="group">
                                            <label className="text-xs font-semibold text-cyan-400/80 mb-2 block uppercase tracking-wider group-focus-within:text-cyan-400 transition-colors">Ad Soyad</label>
                                            <input
                                                type="text"
                                                name="name"
                                                required
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="w-full px-4 py-4 rounded-xl bg-black/20 border border-white/10 focus:border-cyan-500/50 focus:bg-cyan-950/10 focus:ring-4 focus:ring-cyan-500/10 outline-none transition-all text-white placeholder:text-white/20"
                                                placeholder="İsminiz"
                                            />
                                        </div>
                                        <div className="group">
                                            <label className="text-xs font-semibold text-cyan-400/80 mb-2 block uppercase tracking-wider group-focus-within:text-cyan-400 transition-colors">E-posta</label>
                                            <input
                                                type="email"
                                                name="email"
                                                required
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="w-full px-4 py-4 rounded-xl bg-black/20 border border-white/10 focus:border-cyan-500/50 focus:bg-cyan-950/10 focus:ring-4 focus:ring-cyan-500/10 outline-none transition-all text-white placeholder:text-white/20"
                                                placeholder="email@adresiniz.com"
                                            />
                                        </div>
                                    </div>

                                    <div className="group">
                                        <label className="text-xs font-semibold text-cyan-400/80 mb-2 block uppercase tracking-wider group-focus-within:text-cyan-400 transition-colors">Konu</label>
                                        <select
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            className="w-full px-4 py-4 rounded-xl bg-black/20 border border-white/10 focus:border-cyan-500/50 focus:bg-cyan-950/10 focus:ring-4 focus:ring-cyan-500/10 outline-none transition-all text-white appearance-none cursor-pointer"
                                        >
                                            <option value="Sponsorluk" className="bg-slate-900 text-white">Sponsorluk</option>
                                            <option value="İş Birliği" className="bg-slate-900 text-white">İş Birliği</option>
                                            <option value="Genel" className="bg-slate-900 text-white">Genel Soru</option>
                                        </select>
                                    </div>

                                    <div className="group">
                                        <label className="text-xs font-semibold text-cyan-400/80 mb-2 block uppercase tracking-wider group-focus-within:text-cyan-400 transition-colors">Mesajınız</label>
                                        <textarea
                                            name="message"
                                            required
                                            rows="4"
                                            value={formData.message}
                                            onChange={handleChange}
                                            className="w-full px-4 py-4 rounded-xl bg-black/20 border border-white/10 focus:border-cyan-500/50 focus:bg-cyan-950/10 focus:ring-4 focus:ring-cyan-500/10 outline-none transition-all text-white placeholder:text-white/20 resize-none"
                                            placeholder="Size nasıl yardımcı olabiliriz?"
                                        />
                                    </div>

                                    <Button variant="primary" className="w-full py-5 text-lg bg-gradient-to-r from-cyan-600 via-cyan-500 to-cyan-400 hover:from-cyan-500 hover:to-cyan-300 border-none shadow-lg shadow-cyan-500/20 group relative overflow-hidden">
                                        <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                                        <span className="relative flex items-center justify-center gap-3 font-bold tracking-wide">
                                            GÖNDER <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                        </span>
                                    </Button>
                                </form>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </main>


        </div>
    );
};

export default ContactPage;
